import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useEffect, useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useGetApiAttachmentsByVersionIdQuery,
  usePutApiAttachmentsByIdMutation,
} from '../../../redux/store/api/api';
import {
  selectActiveVersion,
  selectContextAttachment,
  selectRenameAttachmentDialogOpened,
  setRenameAttachmentDialogOpened,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';

function RenameAttachmentDialog(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t: translation } = useTranslation();
  const dialogShow = useAppSelector(selectRenameAttachmentDialogOpened);
  const attachment = useAppSelector(selectContextAttachment);
  const version = useAppSelector(selectActiveVersion);
  const [attachmentName, setAttachmentName] = useState(
    attachment.visibleName || attachment.name || '',
  );
  const [attachmentNameIsValid, setAttachmentNameIsValid] = useState(true);
  const [updateAttachment, { isError, isLoading, error }] =
    usePutApiAttachmentsByIdMutation();
  const { refetch } = useGetApiAttachmentsByVersionIdQuery(
    version.id
      ? {
          versionId: version.id,
        }
      : skipToken,
  );

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'UpdateAttachmentError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    setAttachmentName(attachment.visibleName || attachment.name || '');
    setAttachmentNameIsValid(true);
  }, [attachment]);

  const handleUpdateAttachment = () => {
    if (attachmentName.trim().length === 0) {
      setAttachmentNameIsValid(false);
      return;
    }

    updateAttachment({
      id: attachment.id || '',
      namedAttachment: {
        id: attachment.id,
        visibleName: attachmentName.trim(),
      },
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'UpdateAttachmentSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        refetch();
        dispatch(setRenameAttachmentDialogOpened(false));
      });
  };

  return (
    <CustomDialog
      titleId='RenameAttachmentDialog'
      show={dialogShow}
      closeFunction={() => {
        dispatch(setRenameAttachmentDialogOpened(false));
      }}
      closeTitle={translation('cancel')}
      actionFunction={handleUpdateAttachment}
      actionTitle={translation('save')}
      actionButtonDisabled={
        isLoading ||
        attachment.visibleName === attachmentName.trim() ||
        (!attachment.visibleName && attachment.name === attachmentName.trim())
      }
      dialogTitle={translation('renameAttachment')}>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <p>{translation('fieldsAreRequiredLegend')}</p>
          <Form.Group className='mb-3' controlId='AttachmentName'>
            <Form.Label>{translation('name')}*</Form.Label>
            <InputGroup>
              <Form.Control
                required
                isInvalid={!attachmentNameIsValid}
                aria-describedby={
                  !attachmentNameIsValid
                    ? 'AttachmentNameInputError'
                    : undefined
                }
                onChange={(e) => {
                  setAttachmentName(e.target.value);
                  if (e.target.value.trim().length > 0) {
                    setAttachmentNameIsValid(true);
                  }
                }}
                type='text'
                value={attachmentName}
              />
              <Button
                aria-label={translation('restoreOriginalName')}
                title={translation('restoreOriginalName')}
                variant='outline-dark'
                className='rounded-circle circle-btn ms-1'
                onClick={() => {
                  setAttachmentName(attachment.name || '');
                }}>
                <i aria-hidden className='icon-wiederherstellen' />
              </Button>
              <Form.Control.Feedback
                id='AttachmentNameInputError'
                type='invalid'>
                {translation('fieldNotEmpty')}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </>
      )}
    </CustomDialog>
  );
}

export default RenameAttachmentDialog;
